import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Offers.css';

interface Publisher {
  id: number;
  name: string;
  email: string;
  api_key: string;
  tier: string;
  status: string;
}

const PublisherAPI: React.FC = () => {
  const navigate = useNavigate();
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

  useEffect(() => {
    fetchPublishers();
  }, []);

  const fetchPublishers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiBaseUrl}/api/publishers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPublishers(data.publishers || []);
      }
    } catch (error) {
      console.error('Error fetching publishers:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const regenerateApiKey = async (publisherId: number) => {
    if (!window.confirm('Are you sure you want to regenerate the API key? The old key will stop working immediately.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiBaseUrl}/api/admin/publishers/${publisherId}/regenerate-api-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('API key regenerated successfully!');
        fetchPublishers();
        if (selectedPublisher?.id === publisherId) {
          const updatedPublisher = publishers.find(p => p.id === publisherId);
          if (updatedPublisher) {
            setSelectedPublisher(updatedPublisher);
          }
        }
      } else {
        alert('Failed to regenerate API key');
      }
    } catch (error) {
      console.error('Error regenerating API key:', error);
      alert('Error regenerating API key');
    }
  };

  if (loading) {
    return <div className="offers-page"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="offers-page">
      <div className="page-header">
        <h1>Publisher API Documentation</h1>
        <p>External API for publishers to fetch offers and statistics</p>
      </div>

      {/* Publisher Selection */}
      <div className="offers-section">
        <div className="section-header">
          <h2>Select Publisher</h2>
        </div>
        <div className="table-container">
          <table className="offers-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Publisher Name</th>
                <th>Email</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {publishers.map((publisher) => {
                const isSelected = selectedPublisher?.id === publisher.id;
                const tdStyle = isSelected ? { color: '#000', fontWeight: '500' } : {};
                const badgeStyle = isSelected ? { color: '#000' } : {};

                return (
                  <tr
                    key={publisher.id}
                    style={{
                      backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                    }}
                  >
                    <td style={tdStyle}>{publisher.id}</td>
                    <td style={tdStyle}>{publisher.name}</td>
                    <td style={tdStyle}>{publisher.email}</td>
                    <td>
                      <span className={`tier-badge tier-${publisher.tier}`} style={badgeStyle}>
                        {publisher.tier.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${publisher.status}`} style={badgeStyle}>
                        {publisher.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setSelectedPublisher(publisher)}
                        style={{ marginRight: '5px' }}
                      >
                        View API Docs
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Documentation */}
      {selectedPublisher && (
        <>
          {/* API Credentials */}
          <div className="offers-section">
            <div className="section-header">
              <h2>API Credentials for {selectedPublisher.name}</h2>
            </div>
            <div className="table-container">
              <table className="offers-table">
                <thead>
                  <tr>
                    <th style={{ width: '200px' }}>Credential</th>
                    <th>Value</th>
                    <th style={{ width: '300px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Base URL</strong></td>
                    <td>
                      <code style={{
                        padding: '4px 8px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#495057'
                      }}>
                        {apiBaseUrl}
                      </code>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => copyToClipboard(apiBaseUrl)}
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>API Key</strong></td>
                    <td>
                      <code style={{
                        padding: '4px 8px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        color: '#495057'
                      }}>
                        {showApiKey ? selectedPublisher.api_key : '••••••••••••••••••••••••••••••••'}
                      </code>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShowApiKey(!showApiKey)}
                        style={{ marginRight: '5px' }}
                      >
                        {showApiKey ? 'Hide' : 'Show'}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => copyToClipboard(selectedPublisher.api_key)}
                        style={{ marginRight: '5px' }}
                      >
                        Copy
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => regenerateApiKey(selectedPublisher.id)}
                      >
                        Regenerate
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{
                marginTop: '15px',
                padding: '12px',
                backgroundColor: '#fff3cd',
                borderRadius: '4px',
                border: '1px solid #ffc107',
                fontSize: '14px',
                color: '#dc3545'
              }}>
                <strong>⚠️ Important:</strong> Keep your API key secure. Do not share it publicly or commit it to version control.
              </div>
            </div>
          </div>

          {/* Authentication */}
          <div className="offers-section">
            <div className="section-header">
              <h2>Authentication</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <p>Include your API key in the request header:</p>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`X-API-Key: ${selectedPublisher.api_key}`}
              </pre>
              <p style={{ marginTop: '10px' }}>Or as a query parameter:</p>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`${apiBaseUrl}/api/external/publisher/offers?api_key=${selectedPublisher.api_key}`}
              </pre>
            </div>
          </div>

          {/* Endpoint 1: Get Offers */}
          <div className="offers-section">
            <div className="section-header">
              <h2>GET /api/external/publisher/offers</h2>
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#28a745',
                color: 'white',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                GET
              </span>
            </div>
            <div style={{ padding: '20px' }}>
              <p><strong>Description:</strong> Retrieve all approved offers assigned to your publisher account.</p>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Request Example (Using Header):</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`curl -X GET "${apiBaseUrl}/api/external/publisher/offers" \\
  -H "X-API-Key: ${selectedPublisher.api_key}"`}
              </pre>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Request Example (Using Query Parameter):</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`curl -X GET "${apiBaseUrl}/api/external/publisher/offers?api_key=${selectedPublisher.api_key}"`}
              </pre>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Response Example:</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
{`{
  "success": true,
  "count": 2,
  "publisher": {
    "id": ${selectedPublisher.id},
    "name": "${selectedPublisher.name}"
  },
  "offers": [
    {
      "offer_id": "29",
      "offer_name": "Sample Campaign",
      "advertiser": {
        "id": 27,
        "name": "D-LAB"
      },
      "payout": 4.00,
      "payout_currency": "USD",
      "payout_model": "CPI",
      "category": "Non Incent",
      "daily_cap": 1000,
      "monthly_cap": null,
      "tracking_url": "${apiBaseUrl}/tracking?offer_id=29&aff_id=${selectedPublisher.id}"
    }
  ]
}`}
              </pre>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Response Fields:</h3>
              <table className="offers-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>offer_id</code></td>
                    <td>string</td>
                    <td>Unique identifier for the offer</td>
                  </tr>
                  <tr>
                    <td><code>offer_name</code></td>
                    <td>string</td>
                    <td>Name of the offer/campaign</td>
                  </tr>
                  <tr>
                    <td><code>payout</code></td>
                    <td>number</td>
                    <td>Your payout per conversion</td>
                  </tr>
                  <tr>
                    <td><code>payout_model</code></td>
                    <td>string</td>
                    <td>Type of conversion (CPI, CPA, CPL, etc.)</td>
                  </tr>
                  <tr>
                    <td><code>tracking_url</code></td>
                    <td>string</td>
                    <td>URL to use for tracking clicks and conversions</td>
                  </tr>
                  <tr>
                    <td><code>daily_cap</code></td>
                    <td>number</td>
                    <td>Maximum conversions per day (null = unlimited)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Endpoint 2: Get Stats */}
          <div className="offers-section">
            <div className="section-header">
              <h2>GET /api/external/publisher/stats</h2>
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#28a745',
                color: 'white',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                GET
              </span>
            </div>
            <div style={{ padding: '20px' }}>
              <p><strong>Description:</strong> Retrieve performance statistics for your campaigns.</p>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Query Parameters:</h3>
              <table className="offers-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>start_date</code></td>
                    <td>string</td>
                    <td>No</td>
                    <td>Start date (YYYY-MM-DD format)</td>
                  </tr>
                  <tr>
                    <td><code>end_date</code></td>
                    <td>string</td>
                    <td>No</td>
                    <td>End date (YYYY-MM-DD format)</td>
                  </tr>
                  <tr>
                    <td><code>offer_id</code></td>
                    <td>number</td>
                    <td>No</td>
                    <td>Filter by specific offer ID</td>
                  </tr>
                </tbody>
              </table>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Request Example (Using Header):</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`curl -X GET "${apiBaseUrl}/api/external/publisher/stats?start_date=2025-01-01&end_date=2025-01-31" \\
  -H "X-API-Key: ${selectedPublisher.api_key}"`}
              </pre>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Request Example (Using Query Parameter):</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`curl -X GET "${apiBaseUrl}/api/external/publisher/stats?api_key=${selectedPublisher.api_key}&start_date=2025-01-01&end_date=2025-01-31"`}
              </pre>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Response Example:</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
{`{
  "success": true,
  "publisher": {
    "id": ${selectedPublisher.id},
    "name": "${selectedPublisher.name}"
  },
  "stats": {
    "clicks": 1523,
    "conversions": 87,
    "earnings": "348.00",
    "currency": "USD",
    "conversion_rate": "5.71",
    "epc": "0.2286"
  },
  "filters": {
    "start_date": "2025-01-01",
    "end_date": "2025-01-31",
    "offer_id": null
  }
}`}
              </pre>

              <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Response Fields:</h3>
              <table className="offers-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>clicks</code></td>
                    <td>number</td>
                    <td>Total number of clicks</td>
                  </tr>
                  <tr>
                    <td><code>conversions</code></td>
                    <td>number</td>
                    <td>Total number of conversions</td>
                  </tr>
                  <tr>
                    <td><code>earnings</code></td>
                    <td>string</td>
                    <td>Total earnings (formatted to 2 decimals)</td>
                  </tr>
                  <tr>
                    <td><code>conversion_rate</code></td>
                    <td>string</td>
                    <td>Percentage of clicks that converted</td>
                  </tr>
                  <tr>
                    <td><code>epc</code></td>
                    <td>string</td>
                    <td>Earnings Per Click</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Integration Guide */}
          <div className="offers-section">
            <div className="section-header">
              <h2>Integration Guide</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <h3>Step 1: Fetch Available Offers</h3>
              <p>Call the <code>/api/external/publisher/offers</code> endpoint to get your approved offers.</p>

              <h3 style={{ marginTop: '20px' }}>Step 2: Use Tracking URLs</h3>
              <p>Each offer includes a <code>tracking_url</code>. Direct your traffic to this URL to track clicks and conversions.</p>
              <p>You can append custom parameters to the tracking URL for your own tracking:</p>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`${apiBaseUrl}/tracking?offer_id=29&aff_id=${selectedPublisher.id}&aff_sub1=YOUR_CUSTOM_ID&aff_sub2=SOURCE`}
              </pre>

              <h3 style={{ marginTop: '20px' }}>Step 3: Monitor Performance</h3>
              <p>Use the <code>/api/external/publisher/stats</code> endpoint to track your performance metrics.</p>

              <h3 style={{ marginTop: '20px' }}>Step 4: Handle Conversions</h3>
              <p>Conversions are automatically tracked via our postback system. You'll see them reflected in your stats.</p>

              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d1ecf1', borderRadius: '4px', border: '1px solid #bee5eb' }}>
                <strong>Payout Information:</strong> Payouts are specific to each offer and are shown in the offers list. Your earnings are calculated based on your approved offers and custom payout agreements.
              </div>
            </div>
          </div>

          {/* Error Handling */}
          <div className="offers-section">
            <div className="section-header">
              <h2>Error Handling</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <h3>Common Error Responses:</h3>
              <table className="offers-table" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th>Status Code</th>
                    <th>Error</th>
                    <th>Solution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>401</td>
                    <td>Missing API key</td>
                    <td>Include X-API-Key header or api_key parameter</td>
                  </tr>
                  <tr>
                    <td>401</td>
                    <td>Invalid API key</td>
                    <td>Check your API key is correct and account is active</td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td>Internal server error</td>
                    <td>Contact support if the issue persists</td>
                  </tr>
                </tbody>
              </table>

              <h3 style={{ marginTop: '20px' }}>Example Error Response:</h3>
              <pre style={{
                backgroundColor: '#282c34',
                color: '#abb2bf',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto'
              }}>
{`{
  "success": false,
  "error": "Invalid API key",
  "message": "API key not found or publisher inactive"
}`}
              </pre>
            </div>
          </div>

          {/* Support */}
          <div className="offers-section">
            <div className="section-header">
              <h2>Support</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <p>For technical support or questions about the API, please contact:</p>
              <ul style={{ marginTop: '10px', lineHeight: '1.8' }}>
                <li><strong>Email:</strong> support@adbizz.com</li>
                <li><strong>Documentation:</strong> This page</li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PublisherAPI;
